import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Container,
  IconButton,
  Menu,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import {
  getAllTests,
  createTest,
  updateTest,
  deleteTest,
  clearError,
  resetTestStates,
} from "../../redux/testSlice";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";

// Note: All other files also need to be updated to English. Here are the key changes needed:

// In QuestionFormFields.jsx:
// - "Option text cannot be empty!" -> already in English
// - "Please add at least 2 options" -> already in English
// - All labels and placeholders -> already in English

// In AdminTestDetails.jsx:
// - All success messages -> already in English
// - All UI text -> already in English

// In AddQuestionDialog.jsx:
// - "Please enter question text and type!" -> already in English
// - "You must add at least 2 options!" -> already in English

// In EditQuestionDialog.jsx:
// - All text -> already in English

// In DeleteQuestionDialog.jsx:
// - All text -> already in English

// In EditTestDialog.jsx:
// - All text -> already in English

// In QuestionCard.jsx:
// - All text -> already in English

// In QuestionList.jsx:
// - All text -> already in English

// In TestInfoCard.jsx:
// - All text -> already in English

const AdminTestsPage = () => {
  const navigate = useNavigate();
  const [createTestDialog, setCreateTestDialog] = useState(false);
  const [editTestDialog, setEditTestDialog] = useState(false);
  const [deleteTestDialog, setDeleteTestDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const dispatch = useDispatch();
  const testsState = useSelector((state) => state.tests);

  const {
    tests = [],
    loading = false,
    error = null,
    createTestSuccess = false,
    updateTestSuccess = false,
    deleteTestSuccess = false,
  } = testsState || {};

  // Test creation form state
  const [newTest, setNewTest] = useState({
    name: "",
    description: "",
    type: "",
    instructions: "",
    imageUrl: "",
  });

  // Test edit form state
  const [editTest, setEditTest] = useState({
    name: "",
    description: "",
    type: "",
    instructions: "",
    imageUrl: "",
  });

  useEffect(() => {
    dispatch(getAllTests());
  }, [dispatch]);

  useEffect(() => {
    if (createTestSuccess) {
      toast.success("Test created successfully!");
      setCreateTestDialog(false);
      setNewTest({
        name: "",
        description: "",
        type: "",
        instructions: "",
        imageUrl: "",
      });
      dispatch(resetTestStates());
      dispatch(getAllTests());
    }
  }, [createTestSuccess, dispatch]);

  useEffect(() => {
    if (updateTestSuccess) {
      toast.success("Test updated successfully!");
      setEditTestDialog(false);
      setSelectedTest(null);
      dispatch(resetTestStates());
      dispatch(getAllTests());
    }
  }, [updateTestSuccess, dispatch]);

  useEffect(() => {
    if (deleteTestSuccess) {
      toast.success("Test deleted successfully!");
      setDeleteTestDialog(false);
      setSelectedTest(null);
      dispatch(resetTestStates());
    }
  }, [deleteTestSuccess, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleTestSelect = (testId) => {
    navigate(`/adminTests/${testId}`);
  };

  const handleCreateTest = () => {
    if (
      !newTest.name.trim() ||
      !newTest.description.trim() ||
      !newTest.type ||
      !newTest.instructions.trim()
    ) {
      toast.error("Please fill in all required fields!");
      return;
    }
    dispatch(createTest({
      ...newTest,
      name: newTest.name.trim(),
      description: newTest.description.trim(),
      instructions: newTest.instructions.trim(),
    }));
  };

  const handleMenuClick = (event, test) => {
    setAnchorEl(event.currentTarget);
    setSelectedTest(test);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    if (selectedTest) {
      setEditTest({
        name: selectedTest.name,
        description: selectedTest.description,
        type: selectedTest.type,
        instructions: selectedTest.instructions,
        imageUrl: selectedTest.imageUrl || "",
      });
      setEditTestDialog(true);
    }
    handleMenuClose();
  };

  const handleUpdateTest = () => {
    if (
      !editTest.name.trim() ||
      !editTest.description.trim() ||
      !editTest.type ||
      !editTest.instructions.trim()
    ) {
      toast.error("Please fill in all required fields!");
      return;
    }

    dispatch(
      updateTest({
        testId: selectedTest._id,
        testData: {
          ...editTest,
          name: editTest.name.trim(),
          description: editTest.description.trim(),
          instructions: editTest.instructions.trim(),
        },
      }),
    );
  };

  const handleDeleteClick = () => {
    if (selectedTest) {
      setDeleteTestDialog(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    console.log("selectedTest:", selectedTest);
    if (selectedTest) {
      dispatch(deleteTest(selectedTest._id));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTestDialog(false);
    setSelectedTest(null);
  };

  const handleEditCancel = () => {
    setEditTestDialog(false);
    setSelectedTest(null);
  };

  const testTypes = [
    { value: "SURVEY", label: "Survey" },
    { value: "DEPRESSION", label: "Depression" },
    { value: "ANXIETY", label: "Anxiety" },
    { value: "EMOTIONAL_INTELLIGENCE", label: "Emotional Intelligence" },
    { value: "EMPATHY", label: "Empathy" },
    { value: "MENTAL_WELLBEING", label: "Mental Wellbeing" },
    { value: "SOCIAL_PHOBIA", label: "Social Phobia" },
    { value: "WHITE_BEAR_SUPPRESSION", label: "White Bear Suppression" },
    { value: "EATING_BEHAVIOR", label: "Eating Behavior" },
  ];

  return (
    <>
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <Container maxWidth="xl">
            <Box mb={4}>
              <Typography variant="h4" fontWeight="600" gutterBottom>
                Test Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create, edit and manage psychological tests
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} mb={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateTestDialog(true)}
                disabled={loading}
              >
                Create New Test
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => dispatch(getAllTests())}
                disabled={loading}
              >
                Refresh Tests
              </Button>
            </Stack>

            {loading && (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            )}

            <Grid container spacing={3}>
              {tests
                .filter((test) => test.type !== "REGISTRATION")
                .map((test) => (
                  <Grid item xs={12} md={6} lg={4} key={test._id}>
                    <Card>
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box flex={1}>
                            <Typography variant="h6" gutterBottom>
                              {test.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              mb={2}
                            >
                              {test.description}
                            </Typography>
                          </Box>
                          <IconButton onClick={(e) => handleMenuClick(e, test)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                        <Stack direction="row" spacing={1} mt={2}>
                          <Chip
                            label={
                              testTypes.find((t) => t.value === test.type)
                                ?.label || test.type
                            }
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={test.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={test.isActive ? "success" : "default"}
                          />
                        </Stack>
                      </CardContent>
                      <CardActions
                        sx={{
                          borderTop: "1px solid #eee",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleTestSelect(test._id)}
                        >
                          Manage Questions
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              {tests.filter((test) => test.type !== "REGISTRATION").length ===
                0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: "center", bgcolor: "#f5f5f5" }}>
                    <Typography color="text.secondary">
                      No psychological tests found. Create your first test
                      above!
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>

            {/* Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleEditClick}>
                <EditIcon sx={{ mr: 1 }} fontSize="small" />
                Edit Test
              </MenuItem>
              <MenuItem
                onClick={handleDeleteClick}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                Delete Test
              </MenuItem>
            </Menu>

            {tests.length === 0 && !loading && (
              <Paper sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No tests found
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Click the button to create your first test
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateTestDialog(true)}
                >
                  Create First Test
                </Button>
              </Paper>
            )}
          </Container>

          {/* Create Test Dialog */}
          <Dialog
            open={createTestDialog}
            onClose={() => setCreateTestDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Create New Test</DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  required
                  label="Test Name"
                  value={newTest.name}
                  onChange={(e) =>
                    setNewTest({ ...newTest, name: e.target.value })
                  }
                  error={newTest.name !== "" && !newTest.name.trim()}
                  helperText={newTest.name !== "" && !newTest.name.trim() ? "Name cannot be only spaces" : ""}
                />
                <TextField
                  fullWidth
                  required
                  label="Description"
                  multiline
                  rows={3}
                  value={newTest.description}
                  onChange={(e) =>
                    setNewTest({ ...newTest, description: e.target.value })
                  }
                  error={newTest.description !== "" && !newTest.description.trim()}
                  helperText={newTest.description !== "" && !newTest.description.trim() ? "Description cannot be only spaces" : ""}
                />
                <FormControl fullWidth required>
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    value={newTest.type}
                    label="Test Type"
                    onChange={(e) =>
                      setNewTest({ ...newTest, type: e.target.value })
                    }
                  >
                    {testTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  required
                  label="Instructions"
                  multiline
                  rows={4}
                  value={newTest.instructions}
                  onChange={(e) =>
                    setNewTest({ ...newTest, instructions: e.target.value })
                  }
                  error={newTest.instructions !== "" && !newTest.instructions.trim()}
                  helperText={newTest.instructions !== "" && !newTest.instructions.trim() ? "Instructions cannot be only spaces" : ""}
                />
                <TextField
                  fullWidth
                  label="Image URL"
                  value={newTest.imageUrl}
                  onChange={(e) =>
                    setNewTest({ ...newTest, imageUrl: e.target.value })
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateTestDialog(false)}>Cancel</Button>
              <Button
                onClick={handleCreateTest}
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "Create"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Test Dialog */}
          <Dialog
            open={editTestDialog}
            onClose={handleEditCancel}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Edit Test</DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  label="Test Name *"
                  value={editTest.name}
                  onChange={(e) =>
                    setEditTest({ ...editTest, name: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  label="Description *"
                  multiline
                  rows={3}
                  value={editTest.description}
                  onChange={(e) =>
                    setEditTest({ ...editTest, description: e.target.value })
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>Test Type *</InputLabel>
                  <Select
                    value={editTest.type}
                    label="Test Type *"
                    onChange={(e) =>
                      setEditTest({ ...editTest, type: e.target.value })
                    }
                  >
                    {testTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Instructions *"
                  multiline
                  rows={4}
                  value={editTest.instructions}
                  onChange={(e) =>
                    setEditTest({ ...editTest, instructions: e.target.value })
                  }
                />
                <TextField
                  fullWidth
                  label="Image URL"
                  value={editTest.imageUrl}
                  onChange={(e) =>
                    setEditTest({ ...editTest, imageUrl: e.target.value })
                  }
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditCancel}>Cancel</Button>
              <Button
                onClick={handleUpdateTest}
                variant="contained"
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : "Update"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Test Confirmation Dialog */}
          <Dialog open={deleteTestDialog} onClose={handleDeleteCancel}>
            <DialogTitle>Delete Test</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this test? This action cannot be
                undone and all questions and answers related to this test will
                be deleted.
              </Typography>
              {selectedTest && (
                <Typography variant="body2" color="text.secondary" mt={2}>
                  <strong>Test to be deleted:</strong> {selectedTest.name}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteCancel}>Cancel</Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </div>
    </>
  );
};

export default AdminTestsPage;
